import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LogoDrawing from './LogoDrawing';
import FlowingLines from './FlowingLines';
import { sectionId } from '../utils/routes';

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'it';
  const [isLoaded, setIsLoaded] = useState(false);
  const [logoComplete, setLogoComplete] = useState(false);
  // Initialize isMobile immediately to avoid flash
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 0.95]);
  const y = useTransform(scrollYProgress, [0, 0.3], [0, 100]);

  // Capture viewport height once on mobile to avoid layout shift
  const [mobileHeight, setMobileHeight] = useState(null);

  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 100);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    // Set fixed height once on mobile
    if (window.innerWidth < 768) {
      setMobileHeight(window.innerHeight);
    }
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section
      className="relative flex items-center justify-center overflow-hidden bg-[#0a0a0a] md:min-h-screen"
      style={isMobile && mobileHeight ? { minHeight: `${mobileHeight}px` } : undefined}
    >
      {/* Logo drawing animation - fullscreen background */}
      <LogoDrawing onComplete={() => setLogoComplete(true)} />
      
      {/* Flowing lines effect */}
      <FlowingLines variant="hero" />

      {/* Static gradient orbs - CSS only, no JS animation (desktop only for mobile perf) */}
      <div 
        className="hidden md:block absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full opacity-20 animate-float-slow"
        style={{
          background: 'radial-gradient(circle, rgba(255,215,0,0.12) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />
      
      <div 
        className="hidden md:block absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full opacity-15 animate-float-slow-reverse"
        style={{
          background: 'radial-gradient(circle, rgba(255,215,0,0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* Gold accent lines */}
      <motion.div 
        className="absolute top-0 left-[5%] md:left-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#FFD700]/20 to-transparent"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: isLoaded ? 1 : 0 }}
        transition={{ duration: 1.5, delay: 0.5 }}
      />
      <motion.div 
        className="absolute top-0 right-[5%] md:right-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#FFD700]/20 to-transparent"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: isLoaded ? 1 : 0 }}
        transition={{ duration: 1.5, delay: 0.7 }}
      />

      {/* Main content - disable parallax transforms on mobile for Safari compatibility */}
      <motion.div 
        className="relative z-10 text-center px-6 -mt-28 md:mt-0"
        style={isMobile ? {} : { opacity, scale, y }}
      >
        {/* Top label */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <span className="gold-shimmer text-xs tracking-[0.4em] uppercase font-light">
            {t('hero.tagline')}
          </span>
        </motion.div>

        {/* Main title - cinematic reveal with glow */}
        <div className="overflow-hidden mb-4">
          <motion.h1
            className="font-display text-[12vw] md:text-[10vw] lg:text-[8vw] leading-[0.85] text-white"
            initial={{ y: '100%' }}
            animate={{ y: isLoaded ? 0 : '100%' }}
            transition={{ duration: 1, delay: 0.5, ease: [0.33, 1, 0.68, 1] }}
            style={{
              textShadow: '0 0 60px rgba(255, 255, 255, 0.1)',
            }}
          >
            DOUBLE
          </motion.h1>
        </div>
        
        <div className="overflow-hidden mb-8">
          <motion.h1
            className="font-display text-[12vw] md:text-[10vw] lg:text-[8vw] leading-[0.85]"
            initial={{ y: '100%' }}
            animate={{ y: isLoaded ? 0 : '100%' }}
            transition={{ duration: 1, delay: 0.7, ease: [0.33, 1, 0.68, 1] }}
          >
            <span className="text-outline text-white">A</span>
            <span className="gold-shimmer"> STUDIO</span>
          </motion.h1>
        </div>

        {/* Tagline */}
        <motion.p
          className="font-serif italic text-white/85 text-lg md:text-xl max-w-xl mx-auto mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: isLoaded ? 1 : 0 }}
          transition={{ duration: 1, delay: 1.2 }}
        >
          "{t('hero.quote')}"
        </motion.p>

        {/* CTA */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
          transition={{ duration: 0.8, delay: 1.5 }}
        >
          <motion.button
            onClick={() => { const el = document.getElementById(sectionId(lang, 'contact')); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' }); }}
            className="group relative px-6 sm:px-10 py-3 sm:py-4 text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.3em] uppercase overflow-hidden border border-[#FFD700] text-[#FFD700] hover:text-black transition-colors duration-500 whitespace-nowrap"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              boxShadow: '0 0 20px rgba(255, 215, 0, 0.3), inset 0 0 20px rgba(255, 215, 0, 0.1)'
            }}
          >
            <span className="relative z-10">{t('hero.cta_primary')}</span>
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-[#B8860B] via-[#FFD700] to-[#B8860B]"
              initial={{ x: '-100%' }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.4 }}
            />
          </motion.button>
          
          <motion.button
            onClick={() => { const el = document.getElementById(sectionId(lang, 'portfolio')); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' }); }}
            className="text-white/70 hover:text-white text-xs tracking-[0.3em] uppercase transition-colors duration-500 flex items-center gap-3 group"
            whileHover={{ x: 5 }}
          >
            <span>{t('hero.cta_secondary')}</span>
            <span className="w-8 h-px bg-current transition-all group-hover:w-12" />
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Bottom scroll indicator */}
      <motion.div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0 }}
        transition={{ delay: 2 }}
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-white/70">
          {t('hero.scroll')}
        </span>
        <motion.div
          className="w-px h-16 bg-gradient-to-b from-[#FFD700] to-transparent"
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{ transformOrigin: 'top' }}
        />
      </motion.div>

      {/* Side text */}
      <motion.div
        className="absolute left-8 top-1/2 -translate-y-1/2 hidden lg:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 0.6 : 0 }}
        transition={{ delay: 1.5 }}
      >
        <span className="vertical-text text-[10px] tracking-[0.5em] uppercase text-white/70">
          {t('hero.location')}
        </span>
      </motion.div>

      <motion.div
        className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 0.6 : 0 }}
        transition={{ delay: 1.5 }}
      >
        <span className="vertical-text text-[10px] tracking-[0.5em] uppercase text-white/70">
          {t('hero.expertise')}
        </span>
      </motion.div>
    </section>
  );
};

export default HeroSection;
